import React, { useEffect, useState } from "react";
import client from "../../api/client";
import { Layout } from "../../components/Layout/Layout";
import User from "../../components/User";
import { formatRelative } from "../../utils/formatRelative";
import { EXPRESS_PRICE_KZT } from "../../utils/expressHelpers";
import { CABINET_URL } from "../../config";
import "./index.css";

interface Template { id: number; name: string; }
interface PromoCode {
  id: number;
  code: string;
  influencerId: number;
  discountPercent: number;
  expiresAt: string;
}
interface Sale {
  id: number;
  userId: number | null;
  username: string | null;
  manager: string;
  amount: number;
  currency: string;
  bank: string;
  paymentType: string;
  receipt: string;
  createdAt: string;
  userCreatedAt: string | null;
  orderStatus: string | null;
  promoCode: string | null;
}

const platforms = ["kaspi", "vtb", "stripe", "optima"];

// Поддерживаемые валюты — должны совпадать с enum в backend schema.ts.
const CURRENCIES = ["KZT", "USD", "AZN", "BYN", "GEL", "TRY", "RUB", "EUR", "KGS"] as const;

const PAYMENT_TYPES: { value: string; label: string }[] = [
  { value: "full_payment", label: "Полная оплата" },
  { value: "prepayment", label: "Предоплата" },
  { value: "postpayment", label: "Постоплата" },
  { value: "express_print", label: "Экспресс печать" },
  { value: "express_edit", label: "Ускоренная редактура" },
  { value: "urep", label: "УРЭП" },
  { value: "delivery", label: "Доставка" },
  { value: "additional", label: "Дополнительно" },
];

const paymentTypeLabel = (v: string) =>
  PAYMENT_TYPES.find(p => p.value === v)?.label || v;

// Для попапа «Создать клиента» оставляем только базовые варианты
// (express/delivery/additional — это вторичные транзакции, их менеджер
// добавляет уже после в User.tsx или они генерятся автоматом).
const CREATE_PAYMENT_TYPES: { value: string; label: string }[] = [
  { value: "full_payment", label: "Полная оплата" },
  { value: "prepayment", label: "Предоплата" },
];

const EXPRESS_OPTIONS: { value: string; label: string }[] = [
  { value: "none", label: "Нет" },
  { value: "express_print", label: "Экспресс печать" },
  { value: "express_edit", label: "Ускоренная редактура" },
  { value: "urep", label: "УРЭП" },
];

const genPw = () => String(Math.floor(10000 + Math.random() * 90000));

// Сортировка шаблонов:
//   1. Сначала идут «приоритетные» получатели в фиксированном порядке.
//   2. Остальные — по алфавиту.
//   3. Внутри одного получателя варианты группируются (одинаковые модификаторы
//      рядом), языки идут друг за другом в порядке ru → en → kk/kz → uk → …
//
// Имя шаблона ожидается в формате "<Получатель> [— <модификатор>] (<lang>)",
// напр. "Парню (en)" или "Маме — расширенный (ru)". Если не парсится —
// падает в конец списка по алфавиту.
const PRIORITY_RECIPIENTS = ["парню", "девушке", "маме", "папе", "родителям"];
const LANG_ORDER = ["ru", "en", "kk", "kz", "uk"];

function templateSortKey(name: string): [number, string, number, string, string] {
  const m = name.match(/^(.+?)(?:\s*[—-]\s*(.+?))?\s*\(([^)]+)\)\s*$/);
  if (!m) return [9_999, name.toLowerCase(), 9_999, "", ""];
  const recipient = m[1].trim().toLowerCase();
  const modifier = (m[2] || "").trim().toLowerCase();
  const lang = m[3].trim().toLowerCase();
  const pri = PRIORITY_RECIPIENTS.indexOf(recipient);
  const langIdx = LANG_ORDER.indexOf(lang);
  return [
    pri >= 0 ? pri : 9_999,
    recipient,
    langIdx >= 0 ? langIdx : 9_999,
    lang,
    modifier,
  ];
}

function sortTemplates<T extends { name: string }>(arr: T[]): T[] {
  return [...arr].sort((a, b) => {
    const ka = templateSortKey(a.name);
    const kb = templateSortKey(b.name);
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] < kb[i]) return -1;
      if (ka[i] > kb[i]) return 1;
    }
    return 0;
  });
}

const Sales: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Пагинация: 300 строк на страницу + общее число из /sales/count.
  const PAGE_SIZE = 300;
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  // Tick once a minute so "X мин назад" stays fresh without manual refresh.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // popup state — row click fetches the full order and opens User.tsx
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [rowLoadingId, setRowLoadingId] = useState<number | null>(null);

  // create form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState(genPw());
  const [templateId, setTemplateId] = useState<number | "">("");
  const [payment, setPayment] = useState("");
  const [paymentType, setPaymentType] = useState("full_payment");
  const [platform, setPlatform] = useState("kaspi");
  const [currency, setCurrency] = useState<string>("KZT");
  // Общая сумма заказа (orders.full_amount). Префил — эталонная цена по
  // выбранной валюте; менеджер может снизить, например под свою скидку.
  // От неё считаются сбор постоплаты, доплата клиентом и гейт печати.
  const [fullAmount, setFullAmount] = useState("");
  const [fullPrices, setFullPrices] = useState<Record<string, number>>({});
  const [comment, setComment] = useState("");
  const [commentLogis, setCommentLogis] = useState("");
  const [express, setExpress] = useState("none");
  const [expressAmount, setExpressAmount] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [telegram, setTelegram] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [activePromoCodes, setActivePromoCodes] = useState<PromoCode[]>([]);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // После успешного создания держим логин/пароль отдельно (форма к этому
  // моменту уже сброшена) — чтобы менеджер мог скопировать готовый текст.
  const [created, setCreated] = useState<{ login: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const [allManagers, setAllManagers] = useState<string[]>([]);

  useEffect(() => {
    client.get("/admin/templates").then((r) => setTemplates(sortTemplates(r.data)));
    client
      .get<PromoCode[]>("/admin/promo-codes", { params: { active: "true" } })
      .then((r) => setActivePromoCodes(r.data))
      .catch(() => {});
    // Список менеджеров для фильтра-кнопок. Тянем один раз без фильтра
    // и берём distinct(manager). Не обновляем при изменении filter —
    // иначе кнопки исчезают при выборе одного менеджера.
    client
      .get<Sale[]>("/sales", { params: { limit: 1000 } })
      .then((r) => {
        const set = new Set<string>();
        for (const s of r.data) if (s.manager) set.add(s.manager);
        setAllManagers(Array.from(set).sort((a, b) => a.localeCompare(b, "ru")));
      })
      .catch(() => {});
  }, []);
  // При смене фильтра сбрасываем на первую страницу — иначе можно остаться
  // на офсете, где для нового фильтра вообще нет данных.
  useEffect(() => { setPage(0); }, [filter]);
  useEffect(() => { fetchSales(); /* eslint-disable-next-line */ }, [filter, page]);

  const fetchSales = async () => {
    setSalesLoading(true);
    try {
      const p: any = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      const cp: any = {};
      if (filter) {
        p.manager = filter;
        cp.manager = filter;
      }
      const [{ data }, { data: cnt }] = await Promise.all([
        client.get("/sales", { params: p }),
        client.get<{ total: number }>("/sales/count", { params: cp }),
      ]);
      setSales(data);
      setTotal(cnt?.total ?? 0);
    } catch {} finally { setSalesLoading(false); }
  };

  // В таблице показываем только основные продажи — полная оплата и предоплата.
  // Вторичные транзакции (постоплата, экспресс, доставка, доп.) сюда не идут.
  const visibleSales = sales.filter(
    (s) => s.paymentType === "full_payment" || s.paymentType === "prepayment"
  );

  // Открываем попап User.tsx: подгружаем полный заказ по username (на бэке
  // /orders/:idOrUsername принимает оба). Если у sale нет username (анонимный
  // Stripe-платёж до регистрации) — клик игнорируем.
  const handleRowClick = async (s: Sale) => {
    if (!s.username) return;
    if (rowLoadingId) return;
    setRowLoadingId(s.id);
    try {
      const { data } = await client.get(`/orders/${s.username}`);
      // /orders/:idOrUsername пока может вернуть order-row без username
      // (если бэк ещё не пересобран после правки) — подстраховываем явно,
      // иначе попап не покажет ни юзернейм, ни пароль.
      setSelectedUser({ username: s.username, ...data });
    } catch (err) {
      console.error("Failed to load order for sale", s.id, err);
    } finally {
      setRowLoadingId(null);
    }
  };

  const handleCloseUser = () => setSelectedUser(null);

  // Поля orders, которые юзер мог поменять (status, адрес и т.п.),
  // могут отразиться в sales-вью — перезапрашиваем список.
  const handleUserUpdate = () => {
    fetchSales();
  };

  // Эталонные цены тянем с бэка, чтобы суммы не жили второй копией на фронте.
  useEffect(() => {
    client
      .get<Record<string, number>>("/admin/full-prices")
      .then((r) => setFullPrices(r.data || {}))
      .catch((err) => console.error("[sales] full-prices fetch failed", err));
  }, []);

  // Сумму не подставляем — только показываем прайс в плейсхолдере. Менеджер
  // вводит её руками осознанно: у части заказов она отличается от прайса
  // (скидка, договорённость), и подставленное значение молча уезжало в базу.
  const applyCurrency = (next: string) => {
    setCurrency(next);
  };

  const resetCreateForm = () => {
    setUsername("");
    setPassword(genPw());
    setTemplateId("");
    setPayment("");
    setPaymentType("full_payment");
    setPlatform("kaspi");
    setCurrency("KZT");
    setFullAmount("");
    setComment("");
    setCommentLogis("");
    setExpress("none");
    setExpressAmount("");
    setWhatsapp("");
    setTelegram("");
    setPromoCode("");
    setMsg(null);
    setCreated(null);
    setCopied(false);
  };

  const copyCredentials = async () => {
    if (!created) return;
    const text =
      `Логин: ${created.login}\n` +
      `Пароль: ${created.password}\n\n` +
      `Сайт для заполнения: ${CABINET_URL}/login`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setMsg({ ok: false, text: "Не удалось скопировать" });
    }
  };

  const openCreate = () => {
    resetCreateForm();
    setShowCreate(true);
  };

  const closeCreate = () => {
    setShowCreate(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !templateId) {
      setMsg({ ok: false, text: "Заполните username и шаблон" });
      return;
    }
    // Общая сумма обязательна: от неё считаются сбор постоплаты, гейт печати
    // и доплата клиентом. Дефолт подставлять нельзя — он бы тихо разошёлся
    // с реальной договорённостью по заказу.
    const fullAmountNum = Math.round(Number(fullAmount));
    if (!fullAmount.trim() || !Number.isFinite(fullAmountNum) || fullAmountNum <= 0) {
      setMsg({ ok: false, text: "Укажите общую сумму заказа" });
      return;
    }
    setCreating(true);
    setMsg(null);
    try {
      const { data } = await client.post("/admin/create-user", {
        username,
        password,
        templateId,
        payment: Number(payment) || 0,
        fullAmount: fullAmountNum,
        paymentType,
        platform,
        currency,
        comment,
        commentLogis,
        express,
        expressAmount: express !== "none" ? Number(expressAmount) || 0 : 0,
        whatsapp: whatsapp || undefined,
        telegram: telegram || undefined,
        promoCode: promoCode || undefined,
      });
      setCreated({ login: data.user.username, password });
      setCopied(false);
      setMsg({ ok: true, text: "Пользователь создан" });
      // Refresh transactions list — new sale row was just created.
      fetchSales();
      // Reset only the user-specific fields; keep popup open so the manager
      // can copy credentials before dismissing.
      setUsername("");
      setPassword(genPw());
      setPayment("");
      setFullAmount("");
      setComment("");
      setCommentLogis("");
      setExpress("none");
      setExpressAmount("");
      setWhatsapp("");
      setTelegram("");
    } catch (err: any) {
      setMsg({ ok: false, text: err.response?.data?.error === "Username already exists" ? "Username занят" : "Ошибка" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="sales-head">
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Продажи</h2>
        <button className="btn" onClick={openCreate}>+ Создать клиента</button>
      </div>

      <div className="filters sales-mgr-filter">
        <button
          type="button"
          className={`sales-mgr-btn${filter === "" ? " sales-mgr-btn--active" : ""}`}
          onClick={() => setFilter("")}
        >
          Все
        </button>
        {allManagers.map((m) => (
          <button
            key={m}
            type="button"
            className={`sales-mgr-btn${filter === m ? " sales-mgr-btn--active" : ""}`}
            onClick={() => setFilter(m)}
          >
            {m}
          </button>
        ))}
      </div>

      {salesLoading ? (
        <div style={{ color: "var(--text-3)" }}>Загрузка...</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Username</th>
              <th>Статус</th>
              <th>Тип оплаты</th>
              <th>Сумма</th>
              <th>Менеджер</th>
              <th>Промокод</th>
              <th>Дата транзакции</th>
              <th>Дата создания юзера</th>
            </tr>
          </thead>
          <tbody>
            {visibleSales.map(s => (
              <tr
                key={s.id}
                className={s.username ? "tbl__row--clickable" : undefined}
                style={{ opacity: rowLoadingId === s.id ? 0.5 : undefined }}
                onClick={() => handleRowClick(s)}
              >
                <td>{s.username || "—"}</td>
                <td>{s.orderStatus || "—"}</td>
                <td>{paymentTypeLabel(s.paymentType)}</td>
                <td>{s.amount?.toLocaleString()} {s.currency}</td>
                <td>{s.manager || "—"}</td>
                <td>{s.promoCode || "—"}</td>
                <td>{formatRelative(s.createdAt)}</td>
                <td>{formatRelative(s.userCreatedAt)}</td>
              </tr>
            ))}
            {!visibleSales.length && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", color: "var(--text-3)" }}>
                  Нет транзакций
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {(() => {
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        if (totalPages <= 1) return null;
        // Показываем: 1, current-1, current, current+1, totalPages — с эллипсисами.
        const cur = page + 1;
        const wanted = new Set([1, totalPages, cur - 1, cur, cur + 1]);
        const visible = Array.from(wanted)
          .filter((p) => p >= 1 && p <= totalPages)
          .sort((a, b) => a - b);
        const items: (number | "…")[] = [];
        let prev = 0;
        for (const p of visible) {
          if (prev && p - prev > 1) items.push("…");
          items.push(p);
          prev = p;
        }
        return (
          <div className="sales-pagination">
            <button
              className="sales-page sales-page--arrow"
              disabled={page === 0 || salesLoading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              aria-label="Предыдущая"
            >
              ‹
            </button>
            {items.map((it, i) =>
              it === "…" ? (
                <span key={`e${i}`} className="sales-page-ellipsis">…</span>
              ) : (
                <button
                  key={it}
                  className={`sales-page${cur === it ? " sales-page--active" : ""}`}
                  disabled={salesLoading}
                  onClick={() => setPage(it - 1)}
                >
                  {it}
                </button>
              )
            )}
            <button
              className="sales-page sales-page--arrow"
              disabled={page >= totalPages - 1 || salesLoading}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Следующая"
            >
              ›
            </button>
          </div>
        );
      })()}

      {selectedUser && (
        <User
          user={selectedUser}
          onClose={handleCloseUser}
          onUpdate={handleUserUpdate}
          deadline="—"
        />
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={closeCreate}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Создать клиента</h3>
              <button className="modal-close" onClick={closeCreate} aria-label="Закрыть">×</button>
            </div>

            {msg && <div className={`msg ${msg.ok ? "msg--ok" : "msg--err"}`}>{msg.text}</div>}

            {created && (
              <div className="created-box">
                <div className="created-box__creds">
                  <div>Логин: <b>{created.login}</b></div>
                  <div>Пароль: <b>{created.password}</b></div>
                </div>
                <button type="button" className="btn" onClick={copyCredentials}>
                  {copied ? "Скопировано ✓" : "Скопировать"}
                </button>
              </div>
            )}

            <form className="form" onSubmit={handleCreate}>
              <div className="field">
                <label>Username</label>
                <input value={username} onChange={e => setUsername(e.target.value)} required />
              </div>

              <div className="field">
                <label>Пароль</label>
                <div className="pw-row">
                  <input value={password} onChange={e => setPassword(e.target.value)} />
                  <button type="button" className="gen-btn" onClick={() => setPassword(genPw())}>Новый</button>
                </div>
              </div>

              <div className="field">
                <label>Шаблон</label>
                <select value={templateId} onChange={e => setTemplateId(Number(e.target.value))} required>
                  <option value="">Выберите</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              <div className="form-row">
                <div className="field">
                  <label>Сумма</label>
                  <input type="number" value={payment} onChange={e => setPayment(e.target.value)} placeholder="0" />
                </div>
                <div className="field">
                  <label>Валюта</label>
                  <select value={currency} onChange={e => applyCurrency(e.target.value)}>
                    {CURRENCIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Тип оплаты</label>
                  <select value={paymentType} onChange={e => setPaymentType(e.target.value)}>
                    {CREATE_PAYMENT_TYPES.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label>Общая сумма</label>
                <input
                  type="number"
                  value={fullAmount}
                  onChange={e => setFullAmount(e.target.value)}
                  placeholder={
                    fullPrices[currency] != null
                      ? `по прайсу ${fullPrices[currency]}`
                      : "по прайсу"
                  }
                />
              </div>

              <div className="field">
                <label>Счет</label>
                <select value={platform} onChange={e => setPlatform(e.target.value)}>
                  {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div className="form-row">
                <div className="field">
                  <label>Экспресс</label>
                  <select
                    value={express}
                    onChange={e => {
                      const v = e.target.value;
                      setExpress(v);
                      // Авто-цена для УР/ЭП/УРЭП в KZT. Сумму подставляем,
                      // валюту — тоже KZT, чтобы соответствовало прайсу.
                      const price = (EXPRESS_PRICE_KZT as Record<string, number>)[v];
                      if (price) {
                        setExpressAmount(String(price));
                        applyCurrency("KZT");
                      } else {
                        // 'none' → очищаем поле суммы экспресса.
                        setExpressAmount("");
                      }
                    }}
                  >
                    {EXPRESS_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {express !== "none" && (
                  <div className="field">
                    <label>Сумма экспресса</label>
                    <input
                      type="number"
                      value={expressAmount}
                      onChange={e => setExpressAmount(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="field">
                  <label>WhatsApp (опц.)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={whatsapp}
                    onChange={e => setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 15))}
                    placeholder="77001234567"
                  />
                </div>
                <div className="field">
                  <label>Telegram (опц.)</label>
                  <input
                    type="text"
                    value={telegram}
                    onChange={e => setTelegram(e.target.value)}
                    placeholder="@username или номер"
                  />
                </div>
              </div>

              <div className="field">
                <label>Промокод (опц.)</label>
                <select value={promoCode} onChange={e => setPromoCode(e.target.value)}>
                  <option value="">— нет —</option>
                  {activePromoCodes.map(p => (
                    <option key={p.id} value={p.code}>
                      {p.code} · {p.discountPercent}%
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Комментарий</label>
                <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Необязательно" />
              </div>

              <div className="field">
                <label>Комментарий логистики</label>
                <textarea value={commentLogis} onChange={e => setCommentLogis(e.target.value)} placeholder="Необязательно" />
              </div>

              <button type="submit" className="btn" disabled={creating}>
                {creating ? "Создание..." : "Создать"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Sales;
